import React, { Fragment } from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Grid,
  Typography,
  TextField,
  Container,
  Box,
  createStyles,
  Theme,
  makeStyles, 
  FormControlLabel, 
  Checkbox, Button, FormControl, InputLabel, Select, MenuItem
} from '@material-ui/core';
import { Controller, useForm } from 'react-hook-form';
import { Corporation, Locale, Participant } from "../../core/api/model";
import SpinnerModal from "../../core/spinner-modal";
import ConfirmDialog from "../../core/confirm-dialog";
import { useHistory } from "react-router-dom";

export enum ContactPageType {
  General,
  Meet
}

export enum ContactPageRecipientType {
  Participant = 'participant',
  Corporation = 'corporation',
  Undefined = ''
}

export interface ContactPageProps {
  locale: Locale;
  type: ContactPageType;
  recipientType?: ContactPageRecipientType;
  recipientId?: number;
  expertId?: number;
  showErrorModal: (title: string, description: string, raw?: any) => void;
}

export interface ContactQueryVariables {
  expoCode: string;
  recipientId: number;
}

export interface ContactQueryData {
  sender: Participant[];
  recipient: {
    corpEmail?: string;
    partEmail1?: string;
    partEmail1TypeMain?: boolean;
    partEmail2?: string;
    partEmail2TypMain?: boolean;
  }[];
  recipientCorp?: Corporation[];
}

export const CONTACT_PARTICIPANT_QUERY = gql`
  query Contact($expoCode: String!, $recipientId: Int!) {
    sender: participant (expoCode: $expoCode, currentUser: true) {
      partId,
      partName,
      partSurname,
      partEmail1
    }
    recipient: participant (expoCode: $expoCode, partId: $recipientId) {
      partEmail1
      partEmail1TypeMain
      partEmail2
      partEmail2TypMain
    }
  }
`;

export const CONTACT_CORPORATION_QUERY = gql`
  query Contact($expoCode: String!, $recipientId: Int!) {
    sender: participant (expoCode: $expoCode, currentUser: true) {
      partId,
      partName,
      partSurname,
      partEmail1
    }
    recipient: corporation (expoCode: $expoCode, corpId: $recipientId) {
      corpEmail
    }
  }
`;

export const CONTACT_MUTATION = gql`
  mutation Contact(
    $subject: String!
    $body: String!
    $recipient: String!
    $sender: String!
    $sendCopy: Boolean!
  ) {
      contact (
        subject: $subject
        body: $body
        recipient: $recipient
        sender: $sender
        sendCopy: $sendCopy
      )
      {
        success
      }
  }
`;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    textareaInput: {
      padding: '0 0 5px'
    },
    description: {
      fontWeight: 'bold',
    },
  })
);

export interface ContactFormData {
  senderEmail: string;
  senderName: string;
  senderSurname: string;
  message: string;
  copyToSender: boolean;
  pleaseCallBack: boolean;
  senderPhone: string;
  date: string;
  timeslot: string;
  timezone: string;
}

// @TODO REFACTORING review everything

export const ContactPage = (props: ContactPageProps) => {
  const intl = useIntl();
  const history = useHistory();

  const [date, setDate] = React.useState<Date | null>(null);
  const [copyToSender, setCopyToSender] = React.useState<boolean>(true);
  const [pleaseCallBack, setPleaseCallBack] = React.useState<boolean>(false);
  const [successOpen, setSuccessOpen] = React.useState<boolean>(false);

  const { control, handleSubmit, setValue, errors } = useForm<ContactFormData>();

  const classes = useStyles();

  const getContactQuery = () => {
    switch(props.type) { 
      case ContactPageType.General: { 
        switch(props.recipientType) { 
          case ContactPageRecipientType.Participant: { 
            return CONTACT_PARTICIPANT_QUERY;
          } 
          case ContactPageRecipientType.Corporation: { 
            return CONTACT_CORPORATION_QUERY;
          } 
          default: { 
            return gql``;
          } 
        }
      } 
      case ContactPageType.Meet: { 
        return CONTACT_PARTICIPANT_QUERY;
      } 
      default: { 
        return gql``;
      } 
    }
  }

  const getRecipientId = () => {
    switch(props.type) { 
      case ContactPageType.General: { 
        return props.recipientId || 0;
      } 
      case ContactPageType.Meet: { 
        return props.expertId || 0;
      } 
      default: { 
        return 0;
      } 
    }
  }

  const getRecipientEmail = () => {
    if (props.type === ContactPageType.General && props.recipientType === ContactPageRecipientType.Corporation) {
      return data?.recipient[0].corpEmail || '';
    } else {
      if (data?.recipient[0].partEmail2TypMain) {
        return data?.recipient[0].partEmail2 || '';
      }
      if (data?.recipient[0].partEmail1TypeMain) {
        return data?.recipient[0].partEmail1 || '';
      }
    }
    return '';
  }

  const getEmailSubject = (data: ContactFormData) => {
    switch(props.type) { 
      case ContactPageType.General: { 
        return intl.formatMessage({id: "ContactSubject"}, { senderName: data.senderName, senderSurname: data.senderSurname });
      } 
      case ContactPageType.Meet: { 
        return intl.formatMessage({id: "ContactMeetSubject"}, { senderName: data.senderName, senderSurname: data.senderSurname });
      } 
      default: { 
        return '';
      } 
    }
  }
  
  const getEmailBody = (data: ContactFormData) => {
    switch(props.type) { 
      case ContactPageType.General: { 
        return `${data.message}\n${intl.formatMessage({id: 'ContactMessageFooter'})}`;
      } 
      case ContactPageType.Meet: { 
        let pleaseCallBackText = '';
        if (data.pleaseCallBack) {
          pleaseCallBackText = intl.formatMessage({id: 'ContactMeetBodyPleaseCallBackYes'})
        } else {
          pleaseCallBackText = intl.formatMessage({id: 'ContactMeetBodyPleaseCallBackNo'})
        }
        return intl.formatMessage({id: "ContactMeetBody"}, { ...data, pleaseCallBackText });
      } 
      default: { 
        return '';
      } 
    }
  }
  
  const { data, loading } = 
    useQuery<ContactQueryData, ContactQueryVariables>(
      getContactQuery(), {
        variables: { expoCode: process.env.REACT_APP_EXPO_CODE || '', recipientId: getRecipientId() },
        onCompleted: (_data) => {
          if (
            _data.sender.length === 0 ||
            !_data.sender[0].partEmail1 ||
            _data.recipient.length === 0 ||
            ((props.type === ContactPageType.General && props.recipientType === ContactPageRecipientType.Corporation) && !_data.recipient[0].corpEmail) ||
            ((props.type === ContactPageType.Meet || props.recipientType === ContactPageRecipientType.Participant) && !_data.recipient[0].partEmail1TypeMain && !_data.recipient[0].partEmail2TypMain) ||
            ((props.type === ContactPageType.Meet || props.recipientType === ContactPageRecipientType.Participant) && _data.recipient[0].partEmail1TypeMain && !_data.recipient[0].partEmail1) ||
            ((props.type === ContactPageType.Meet || props.recipientType === ContactPageRecipientType.Participant) && _data.recipient[0].partEmail2TypMain && !_data.recipient[0].partEmail2)
          ) {
            props.showErrorModal(intl.formatMessage({id: 'ContactQueryNoDataTitle'}), intl.formatMessage({id: 'ContactQueryNoDataDescription'}));
          } else {
            setValue('senderEmail', _data?.sender[0]?.partEmail1);
            setValue('senderName', _data?.sender[0]?.partName);
            setValue('senderSurname', _data?.sender[0]?.partSurname);
            setValue('copyToSender', copyToSender);
          }
        },
        onError: (error) => {
          props.showErrorModal(intl.formatMessage({id: 'ContactQueryErrorTitle'}), intl.formatMessage({id: 'ContactQueryErrorDescription'}));
        }
      }
    );

  const [submitContactMutation, {loading: pendingContactMutation}] = useMutation(CONTACT_MUTATION, {
      onCompleted: (_data: { contact: { success: boolean } }) => {
        if (_data.contact.success) {
          setSuccessOpen(true);
        } else {
          props.showErrorModal(intl.formatMessage({id: 'ContactMutationErrorTitle'}), intl.formatMessage({id: 'ContactMutationErrorDescription'}));
        }
      },
      onError: (error) => {
        props.showErrorModal(intl.formatMessage({id: 'ContactMutationErrorTitle'}), intl.formatMessage({id: 'ContactMutationErrorDescription'}));
      }
    }
  );

  const submit = (_data: ContactFormData) => {
    const recipientEmail = getRecipientEmail();
    
    submitContactMutation({ variables: {
      subject: getEmailSubject(_data),
      body: getEmailBody(_data),
      recipient: recipientEmail,
      sender: _data.senderEmail,
      sendCopy: _data.copyToSender,
    }});
  }

  const timeslotOptions: string[] = [];
  for (let i = 8; i < 18; i++) {
    timeslotOptions.push(`${i}:00 - ${i}:30`);
    timeslotOptions.push(`${i}:30 - ${i+1}:00`);
  }

  return (
    <Fragment>
      <SpinnerModal
        active={loading || pendingContactMutation}
      ></SpinnerModal>

      <Box
        pt={{xs: 3, md: 5}}
        pb={{xs: 7.5, md: 10}}
        className='main-background'
      >
        <Container maxWidth="lg">
            <form onSubmit={handleSubmit(submit)}>
              <Box pb={{xs: 4.5, md: 5}}>
                <Typography component='h2' variant='h1' color='primary' gutterBottom>
                  {props.type === ContactPageType.General ? (
                    <FormattedMessage id='ContactTitle' />
                  ) : (
                    <FormattedMessage id='ContactMeetTitle' />
                  )}
                </Typography>
                <Typography className={classes.description}>
                  <FormattedMessage id='ContactMeetDescription' />
                </Typography>
              </Box>

              <Grid container spacing={6}>
                <Grid item xs={12} md={6}>
                  {/* @TODO REFACTORING email validation */}
                  <Controller
                    as={<TextField
                        margin='normal'
                        fullWidth
                        InputProps={{
                          type: 'email'
                        }}
                        error={!!errors.senderEmail}
                    />}
                    name='senderEmail'
                    control={control}
                    rules={{
                      required: true
                    }}
                    label={intl.formatMessage({id: 'ContactSenderEmail'})}
                    defaultValue={data?.sender[0]?.partEmail1 || ''}
                    helperText={errors.senderEmail ? <FormattedMessage id='ContactSenderEmailRequired' /> : ''}
                  />
                  <Controller
                    as={<TextField
                        margin='normal'
                        fullWidth
                        error={!!errors.senderName}
                    />}
                    name='senderName'
                    control={control}
                    rules={{
                      required: true
                    }}
                    label={intl.formatMessage({id: 'ContactSenderName'})}
                    defaultValue={data?.sender[0]?.partName || ''}
                    helperText={errors.senderName ? <FormattedMessage id='ContactSenderNameRequired' /> : ''}
                  />
                  <Controller
                    as={<TextField
                        margin='normal'
                        fullWidth
                        error={!!errors.senderSurname}
                    />}
                    name='senderSurname'
                    control={control}
                    rules={{
                      required: true
                    }}
                    label={intl.formatMessage({id: 'ContactSenderSurname'})}
                    defaultValue={data?.sender[0]?.partSurname || ''}
                    helperText={errors.senderSurname ? <FormattedMessage id='ContactSenderSurnameRequired' /> : ''}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box mt={{xs: -6, md: 0}}>
                    <Controller
                      as={<TextField
                        margin='normal'
                        multiline
                        variant='standard'
                        rows={9}
                        fullWidth
                        InputProps={{
                          className: classes.textareaInput
                        }}
                        error={!!errors.message}
                      />}
                      name='message'
                      control={control}
                      rules={{
                        required: true
                      }}
                      label={intl.formatMessage({id: 'ContactMessage'})}
                      defaultValue={''}
                      helperText={errors.message ? <FormattedMessage id='ContactMessageRequired' /> : ''}
                    />
                  </Box>
                </Grid>
              </Grid>
              {props.type === ContactPageType.Meet ? (
                <Grid container spacing={5} justify='flex-end'>
                  {/* @TODO STYLING */}
                  <Grid item xs={12} md={6}>
                    <Box mt={0.35}>
                      <FormControl margin='normal'>
                        <Controller
                          as={
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={pleaseCallBack}
                                  onChange={(event, value) => {
                                    setPleaseCallBack(value);
                                    setValue('pleaseCallBack', value);
                                  }}
                                />}
                              label="Secondary"
                            />
                          }
                          name='pleaseCallBack'
                          control={control}
                          label={intl.formatMessage({id: 'ContactPleaseCallBack'})}
                        />
                      </FormControl>
                      {/* @TODO REFACTORING add validation */}
                      <Controller
                        as={<TextField
                          margin='normal'
                          fullWidth
                        />}
                        name='senderPhone'
                        control={control}
                        label={intl.formatMessage({id: 'ContactSenderPhone'})}
                        defaultValue={''}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    {/* @TODO REFACTORING add validation */}
                    <Controller
                      as={<TextField
                          margin='normal'
                          fullWidth
                          type='date'
                      />}
                      name='date'
                      control={control}
                      InputLabelProps={{
                          shrink: true
                      }}
                      label={intl.formatMessage({id: 'ContactDate'})}
                      defaultValue={''}
                    />

                    <FormControl margin='normal' fullWidth>
                      <InputLabel
                          id='contactTimeslotLabel'
                          // className='form--white'
                      >
                        <FormattedMessage id='ContactTimeslot' />
                      </InputLabel>
                      <Controller
                        as={
                          <Select
                            labelId='contactTimeslotLabel'
                            // className='form--white'
                            // inputProps={{
                            //   classes: {
                            //     icon: 'form--white',
                            //   }
                            // }}
                          >
                            <MenuItem key='' value=''></MenuItem>
                            {timeslotOptions.map(timeslot => <MenuItem key={timeslot} value={timeslot}>{timeslot}</MenuItem>)}
                          </Select>
                        }
                        name='timeslot'
                        control={control}
                        defaultValue={''}
                      />
                    </FormControl>
                    
                    {/* @TODO REFACTORING duplicate exists */}
                    <FormControl margin='normal' fullWidth>
                      <InputLabel id='timezoneLabel'
                                  // className='form--white'
                      >
                        <FormattedMessage id='ContactTimezone' />
                      </InputLabel>
                      <Controller
                          as={
                            <Select
                                labelId='timezoneLabel'
                                // className='form--white'
                                // inputProps={{
                                //   classes: {
                                //     icon: 'form--white',
                                //   }
                                // }}
                            >
                              <MenuItem key={-12} value={'UTC -12'}>UTC -12</MenuItem>
                              <MenuItem key={-11} value={'UTC -11'}>UTC -11</MenuItem>
                              <MenuItem key={-10} value={'UTC -10'}>UTC -10</MenuItem>
                              <MenuItem key={-9} value={'UTC -9'}>UTC -9</MenuItem>
                              <MenuItem key={-8} value={'UTC -8'}>UTC -8</MenuItem>
                              <MenuItem key={-7} value={'UTC -7'}>UTC -7</MenuItem>
                              <MenuItem key={-6} value={'UTC -6'}>UTC -6</MenuItem>
                              <MenuItem key={-5} value={'UTC -5'}>UTC -5</MenuItem>
                              <MenuItem key={-4} value={'UTC -4'}>UTC -4</MenuItem>
                              <MenuItem key={-3} value={'UTC -3'}>UTC -3</MenuItem>
                              <MenuItem key={-2} value={'UTC -2'}>UTC -2</MenuItem>
                              <MenuItem key={-1} value={'UTC -1'}>UTC -1</MenuItem>
                              <MenuItem key={0} value={'UTC'}>UTC</MenuItem>
                              <MenuItem key={1} value={'UTC +1'}>UTC +1</MenuItem>
                              <MenuItem key={2} value={'UTC +2'}>UTC +2</MenuItem>
                              <MenuItem key={3} value={'UTC +3'}>UTC +3</MenuItem>
                              <MenuItem key={4} value={'UTC +4'}>UTC +4</MenuItem>
                              <MenuItem key={5} value={'UTC +5'}>UTC +5</MenuItem>
                              <MenuItem key={6} value={'UTC +6'}>UTC +6</MenuItem>
                              <MenuItem key={7} value={'UTC +7'}>UTC +7</MenuItem>
                              <MenuItem key={8} value={'UTC +8'}>UTC +8</MenuItem>
                              <MenuItem key={9} value={'UTC +9'}>UTC +9</MenuItem>
                              <MenuItem key={10} value={'UTC +10'}>UTC +10</MenuItem>
                              <MenuItem key={11} value={'UTC +11'}>UTC +11</MenuItem>
                              <MenuItem key={12} value={'UTC +12'}>UTC +12</MenuItem>
                              <MenuItem key={13} value={'UTC +13'}>UTC +13</MenuItem>
                              <MenuItem key={14} value={'UTC +14'}>UTC +14</MenuItem>
                            </Select>
                          }
                          name='timezone'
                          control={control}
                          defaultValue={''}
                      />
                    </FormControl>

                  </Grid>
                </Grid>
              ) : null}
              <Grid container spacing={5} justify='flex-end'>
                <Grid item xs={12} md={6}>
                  <Box display='flex' justifyContent='space-between' flexDirection={{xs: 'column', md: 'row'}} pt={{xs: 0, md: 4}}>
                    <Controller
                      as={
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={copyToSender}
                              onChange={(event, value) => {
                                setCopyToSender(value);
                                setValue('copyToSender', value);
                              }}
                            />}
                          label="Secondary"
                        />
                      }
                      name='copyToSender'
                      control={control}
                      label={intl.formatMessage({id: 'ContactCopyToSender'})}
                    />
                    <Box mt={{xs: 3, md: 0}}>
                      <Button
                        fullWidth
                        color='primary'
                        type='submit'
                      >
                        {props.type === ContactPageType.General ? (
                          <FormattedMessage id='ContactSend' />
                          ) : (
                          <FormattedMessage id='ContactMeetSend' />
                        )}
                      </Button>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </form>
        </Container>
      </Box>

      <ConfirmDialog
        title={intl.formatMessage({id: 'ContactMutationSuccessTitle'})}
        confirmText={intl.formatMessage({id: 'ContactSuccessButton'})}
        open={successOpen}
        setOpen={setSuccessOpen}
        onConfirm={() => history.push('/')}
      >
        <FormattedMessage id='ContactMutationSuccessDescription' />
      </ConfirmDialog>

    </Fragment>
  );
};
